import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { format, parseISO } from 'date-fns';
import { supabase } from '@/lib/supabase';
import { useGuestBooking } from '@/store/guestBooking';
import { formatGBP } from '@/lib/utils';
export default function Payment() {
    const navigate = useNavigate();
    const { basket, contact } = useGuestBooking((s) => ({ basket: s.basket, contact: s.contact }));
    const [stripePromise, setStripePromise] = useState(null);
    const [loadingStripe, setLoadingStripe] = useState(true);
    useEffect(() => {
        supabase
            .from('settings')
            .select('value')
            .eq('key', 'stripe_publishable_key')
            .single()
            .then(({ data }) => {
            const pk = data?.value;
            if (pk && pk !== 'null')
                setStripePromise(loadStripe(pk));
            setLoadingStripe(false);
        });
    }, []);
    if (basket.length === 0 || !contact) {
        navigate('/book', { replace: true });
        return null;
    }
    const totalPence = basket.reduce((s, i) => s + i.amount_pence, 0);
    if (loadingStripe) {
        return (_jsx("div", { className: "min-h-screen flex items-center justify-center", children: _jsx("p", { className: "text-gray-500", children: "Loading\u2026" }) }));
    }
    return (_jsxs("div", { className: "min-h-screen bg-brand-cream", children: [_jsxs("nav", { className: "bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between", children: [_jsx(Link, { to: "/book/review", className: "text-sm text-gray-500 hover:text-gray-700", children: "\u2190 Review" }), _jsx("span", { className: "font-semibold text-gray-900", children: "Payment" }), _jsx("div", {})] }), _jsxs("main", { className: "max-w-xl mx-auto px-4 py-8", children: [_jsx("p", { className: "text-sm text-gray-500 mb-6", children: "Your card details are handled securely by Stripe and never stored by us." }), _jsxs("div", { className: "bg-white border border-gray-200 rounded-xl p-4 mb-6", children: [_jsx("p", { className: "text-xs font-medium text-gray-400 uppercase tracking-wide mb-3", children: "Order summary" }), basket.map((item, i) => (_jsxs("div", { className: "flex justify-between text-sm py-1", children: [_jsxs("span", { className: "text-gray-600", children: [item.service_name, " \u2014 ", format(parseISO(item.booking_date), 'd MMM'), item.booking_time ? ` ${item.booking_time}` : ''] }), _jsx("span", { className: "font-medium", children: formatGBP(item.amount_pence) })] }, i))), _jsxs("div", { className: "flex justify-between font-semibold text-gray-900 pt-2 mt-1 border-t border-gray-100", children: [_jsx("span", { children: "Total" }), _jsx("span", { children: formatGBP(totalPence) })] }), _jsxs("p", { className: "text-xs text-gray-400 mt-2", children: [contact.name, " \u00B7 ", contact.email] })] }), stripePromise ? (_jsx(Elements, { stripe: stripePromise, children: _jsx(GuestPaymentForm, { totalPence: totalPence, contact: contact, basket: basket, onSuccess: (bookingIds) => {
                                useGuestBooking.getState().setConfirmedIds(bookingIds);
                                navigate('/book/confirmation');
                            } }) })) : (_jsx("div", { className: "bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800", children: "Online payment is not yet configured. Please contact Springfield Dog Centre to complete your booking." }))] })] }));
}
function GuestPaymentForm({ totalPence, contact, basket, onSuccess }) {
    const stripe = useStripe();
    const elements = useElements();
    const [paying, setPaying] = useState(false);
    const [error, setError] = useState(null);
    async function handlePay(e) {
        e.preventDefault();
        if (!stripe || !elements)
            return;
        setPaying(true);
        setError(null);
        const cardElement = elements.getElement(CardElement);
        if (!cardElement) {
            setPaying(false);
            return;
        }
        // Create guest bookings as pending_payment via Edge Function (bypasses RLS)
        const { data: intentData, error: intentError } = await supabase.functions.invoke('create-guest-payment-intent', {
            body: {
                amount: totalPence,
                contact,
                bookings: basket,
            },
        });
        if (intentError || !intentData?.client_secret) {
            setError('Payment setup failed. Please try again.');
            setPaying(false);
            return;
        }
        const { error: stripeError } = await stripe.confirmCardPayment(intentData.client_secret, {
            payment_method: {
                card: cardElement,
                billing_details: { name: contact.name, email: contact.email },
            },
        });
        if (stripeError) {
            setError(stripeError.message ?? 'Payment failed — please check your card details and try again.');
            setPaying(false);
            return;
        }
        onSuccess(intentData.booking_ids ?? []);
    }
    return (_jsxs("form", { onSubmit: handlePay, className: "space-y-4", children: [_jsx("div", { className: "border border-gray-300 rounded-lg px-3 py-3 bg-white", children: _jsx(CardElement, { options: { style: { base: { fontSize: '14px', color: '#1f2937', '::placeholder': { color: '#9ca3af' } } } } }) }), error && _jsx("p", { className: "text-sm text-red-600", children: error }), _jsx("button", { type: "submit", disabled: !stripe || paying, className: "w-full py-3 bg-brand-green text-white font-medium rounded-lg hover:bg-brand-green/90 disabled:opacity-50", children: paying ? 'Processing…' : `Pay ${formatGBP(totalPence)}` })] }));
}
